import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { CalendarSync, Save, Plug, Copy, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Loader } from "@/components/dashboard/ui";

export default function AdminIntegrations() {
  const [cfg, setCfg] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [denied, setDenied] = useState(false);
  const navigate = useNavigate();

  const redirectUri = `${window.location.origin}/api/oauth/calendar/callback`;

  const load = () => api.get("/integrations/google-calendar").then(({ data }) => {
    setCfg(data); setClientId(data.client_id || ""); setEnabled(!!data.enabled); setClientSecret("");
  }).catch((e) => { if (e.response?.status === 403) setDenied(true); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { client_id: clientId, enabled };
      if (clientSecret) body.client_secret = clientSecret;
      const { data } = await api.put("/integrations/google-calendar", body);
      setCfg(data); setClientSecret(""); setTestResult(null);
      toast.success("Configuration enregistrée");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true); setTestResult(null);
    try { const { data } = await api.post("/integrations/google-calendar/test"); setTestResult(data); }
    catch (e) { setTestResult({ ok: false, message: apiError(e.response?.data?.detail) }); }
    finally { setTesting(false); }
  };
  const copy = () => { navigator.clipboard.writeText(redirectUri); toast.success("URI copiée"); };

  if (denied) return <Navigate to="/admin" replace />;
  if (cfg === null) return <Loader />;
  return (
    <div>
      <PageHeader title="Intégrations" subtitle="Connectez des services externes. Réservé au super administrateur." />
      <div className="max-w-2xl space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center"><CalendarSync className="h-5 w-5 text-primary" /></div>
            <div><h3 className="font-heading font-semibold text-lg text-slate-900">Google Agenda</h3>
              <p className="text-sm text-slate-500">Synchronisation individuelle des agendas des collaborateurs.</p></div>
            <span className={`ml-auto px-2.5 py-1 rounded-full text-xs font-medium ${cfg.configured ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{cfg.configured ? "Actif" : "Inactif"}</span>
          </div>

          <div className="mt-5 space-y-4">
            <div>
              <Label>Google OAuth Client ID</Label>
              <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxxx.apps.googleusercontent.com" className="mt-1 font-mono-metric text-sm" data-testid="gcal-client-id" />
            </div>
            <div>
              <Label>Google OAuth Client Secret</Label>
              <Input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)}
                placeholder={cfg.secret_set ? "•••••••• (déjà enregistré — laisser vide pour conserver)" : "GOCSPX-..."} className="mt-1 font-mono-metric text-sm" data-testid="gcal-client-secret" />
              <p className="mt-1 text-xs text-slate-400">Le secret n'est jamais réaffiché. Laissez ce champ vide pour conserver la valeur actuelle.</p>
            </div>
            <div>
              <Label>URI de redirection autorisée</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 break-all">{redirectUri}</code>
                <Button size="sm" variant="outline" onClick={copy} data-testid="gcal-copy-uri"><Copy className="h-4 w-4" /></Button>
              </div>
              <p className="mt-1 text-xs text-slate-400">À ajouter dans la console Google Cloud (Identifiants → URI de redirection autorisés).</p>
            </div>
            <label className="flex items-center justify-between pt-3 border-t border-slate-100">
              <span className="text-sm font-medium">Activer Google Agenda</span>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="gcal-enabled" />
            </label>

            {testResult && (
              <div className={`flex items-start gap-2 text-sm rounded-lg p-3 ${testResult.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-rose-50 text-rose-700 border border-rose-100"}`} data-testid="gcal-test-result">
                {testResult.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 mt-0.5 shrink-0" />}
                {testResult.message}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button className="bg-primary" onClick={save} disabled={saving} data-testid="gcal-save"><Save className="h-4 w-4 mr-1.5" />{saving ? "Enregistrement..." : "Enregistrer"}</Button>
              <Button variant="outline" onClick={test} disabled={testing} data-testid="gcal-test"><Plug className="h-4 w-4 mr-1.5" />{testing ? "Test..." : "Tester la configuration"}</Button>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-3">
          <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
          Ces identifiants sont stockés côté serveur et ne sont jamais exposés aux autres utilisateurs ni inclus dans les sauvegardes. Chaque collaborateur autorise ensuite SON propre agenda depuis « Mon calendrier externe ». Obtenez vos identifiants sur console.cloud.google.com (API Google Calendar activée, scopes calendar.events et calendar.freebusy, accès hors-ligne).
        </div>
      </div>
    </div>
  );
}
